import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatINR } from "../lib/api";
import ProductCard from "../components/ProductCard";
import {
    ShieldCheck,
    Lock,
    Truck,
    Heart,
    PackageCheck,
    Sparkles,
    Quote,
    ArrowUpRight,
} from "lucide-react";
import { toast } from "sonner";

const HERO_BG =
    "https://images.unsplash.com/photo-1760224254181-adac9e1a1900?crop=entropy&cs=srgb&fm=jpg&w=2400&q=85";
const DISCREET_BOX =
    "https://static.prod-images.emergentagent.com/jobs/c2a6c813-bc74-478b-8bcd-bf9799b402f8/images/425b6a92ead2d413fca1cde3db2e8c545e21375dc123fc9023e16f03f593591d.png";
const SCULPTURE =
    "https://static.prod-images.emergentagent.com/jobs/c2a6c813-bc74-478b-8bcd-bf9799b402f8/images/c89256b9aeed91319962e3f1ca8b031da19565c93498e4421ede983a9432d6e7.png";
const SILK_RED =
    "https://images.unsplash.com/photo-1628973434123-3e956f203dd6?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85";

const TESTIMONIALS = [
    {
        q: "Finally, an intimate wellness brand that treats me like an adult. The discreet packaging alone was a revelation.",
        a: "Aisha M., Mumbai",
    },
    {
        q: "I ordered the Intimacy Ritual Box for our anniversary. It arrived unbranded, beautifully presented — nothing on the box gives it away.",
        a: "Rhea & Karthik, Bengaluru",
    },
    {
        q: "The formulations are genuinely clean — no glycerin, no fragrance. I've stopped reading intimate-care labels with anxiety.",
        a: "Divya S., Delhi",
    },
];

const TRUST = [
    {
        icon: <ShieldCheck size={18} strokeWidth={1.25} />,
        t: "Discreet packaging",
        d: "Unbranded mailer, V. Atelier label",
    },
    {
        icon: <Lock size={18} strokeWidth={1.25} />,
        t: "Private checkout",
        d: "Zero data sharing, ever",
    },
    {
        icon: <Heart size={18} strokeWidth={1.25} />,
        t: "Adults 18+",
        d: "A boutique for grown-ups",
    },
    {
        icon: <Truck size={18} strokeWidth={1.25} />,
        t: "COD available",
        d: "Pay privately at your door",
    },
];

export default function Home() {
    const [best, setBest] = useState([]);
    const [cats, setCats] = useState([]);
    const [beginner, setBeginner] = useState([]);
    const [luxury, setLuxury] = useState([]);
    const [email, setEmail] = useState("");

    useEffect(() => {
        api.get("/bestsellers").then((r) => setBest(r.data.slice(0, 4)));
        api.get("/categories").then((r) => setCats(r.data));
        api.get("/products?category=beginner-collection").then((r) =>
            setBeginner(r.data.slice(0, 2)),
        );
        api.get("/products?category=luxury-collection").then((r) =>
            setLuxury(r.data.slice(0, 2)),
        );
    }, []);

    const subscribe = async (e) => {
        e.preventDefault();
        if (!email) return;
        try {
            const { data } = await api.post("/newsletter", { email });
            toast.success(data.message || "Welcome to Veloura.");
            setEmail("");
        } catch {
            toast.error("Please enter a valid email.");
        }
    };

    return (
        <div data-testid="home-page">
            {/* Hero */}
            <section
                className="relative overflow-hidden"
                data-testid="hero"
                style={{ height: "min(78vh, 720px)" }}
            >
                <img
                    src={HERO_BG}
                    alt="Dark burgundy silk"
                    loading="eager"
                    fetchpriority="high"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ filter: "brightness(0.5) saturate(1.05)" }}
                />
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(180deg, rgba(10,10,11,0.55) 0%, rgba(10,10,11,0.25) 35%, rgba(10,10,11,0.95) 100%)",
                    }}
                />
                <div className="vl-grain absolute inset-0" />
                <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 h-full flex flex-col justify-end pb-14 md:pb-20">
                    <div
                        className="overline mb-4"
                        style={{ color: "var(--vl-rose)" }}
                        data-testid="hero-overline"
                    >
                        Intimate Wellness Boutique · Adults 18+
                    </div>
                    <h1
                        className="font-serif tracking-tight leading-[0.96] max-w-4xl"
                        style={{
                            color: "var(--vl-text)",
                            fontSize: "clamp(40px, 7.2vw, 112px)",
                        }}
                        data-testid="hero-heading"
                    >
                        Elevate Intimacy.
                        <br />
                        <em
                            style={{
                                color: "var(--vl-rose)",
                                fontStyle: "italic",
                            }}
                        >
                            Embrace Wellness.
                        </em>
                    </h1>
                    <p
                        className="mt-6 max-w-xl text-base sm:text-lg"
                        style={{
                            color: "var(--vl-muted)",
                            lineHeight: 1.75,
                        }}
                        data-testid="hero-subheading"
                    >
                        Premium intimate wellness for confidence, pleasure,
                        and connection. Thoughtfully made. Dermatologist-
                        reviewed. Shipped discreetly across India.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-4 items-center">
                        <Link
                            to="/best-sellers"
                            className="vl-btn vl-btn-solid"
                            data-testid="hero-cta-best"
                        >
                            Shop best sellers
                        </Link>
                        <Link
                            to="/shop"
                            className="vl-btn vl-btn-ghost"
                            data-testid="hero-cta-shop"
                        >
                            Explore full collection
                        </Link>
                    </div>
                </div>
            </section>

            {/* Trust row — immediately below hero */}
            <section
                className="border-t border-b"
                style={{
                    background: "var(--vl-surface)",
                    borderColor: "var(--vl-border)",
                }}
                data-testid="trust-bar"
            >
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-2 lg:grid-cols-4">
                    {TRUST.map((t, i) => (
                        <div
                            key={t.t}
                            className="flex items-start gap-4 py-6 px-2"
                            style={{
                                borderRight:
                                    i < TRUST.length - 1
                                        ? "1px solid var(--vl-border)"
                                        : "none",
                            }}
                            data-testid={`trust-${i}`}
                        >
                            <span style={{ color: "var(--vl-rose)" }}>
                                {t.icon}
                            </span>
                            <div>
                                <div
                                    className="text-sm"
                                    style={{ color: "var(--vl-text)" }}
                                >
                                    {t.t}
                                </div>
                                <div
                                    className="text-[11px] mt-1"
                                    style={{ color: "var(--vl-muted)" }}
                                >
                                    {t.d}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Social proof stats strip */}
            <section
                className="max-w-[1440px] mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-4"
                data-testid="stats-strip"
            >
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 text-center md:text-left">
                    {[
                        { k: "10,400+", v: "Private orders shipped" },
                        { k: "4.9 / 5", v: "From 1,200+ reviews" },
                        { k: "100%", v: "Unbranded packaging" },
                        { k: "14-day", v: "No-question returns" },
                    ].map((s) => (
                        <div key={s.k}>
                            <div
                                className="font-serif text-3xl sm:text-4xl tracking-tight"
                                style={{ color: "var(--vl-rose)" }}
                            >
                                {s.k}
                            </div>
                            <div
                                className="overline mt-2"
                                style={{ color: "var(--vl-muted)" }}
                            >
                                {s.v}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Best sellers — high priority, right after hero */}
            <section
                className="max-w-[1440px] mx-auto px-6 md:px-12 pt-10 md:pt-14 pb-20"
                data-testid="bestsellers-section"
            >
                <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
                    <div>
                        <div className="overline mb-3">01 · Most loved</div>
                        <h2
                            className="font-serif text-4xl sm:text-6xl tracking-tight"
                            style={{ color: "var(--vl-text)" }}
                        >
                            Best{" "}
                            <em
                                style={{
                                    color: "var(--vl-rose)",
                                    fontStyle: "italic",
                                }}
                            >
                                sellers.
                            </em>
                        </h2>
                    </div>
                    <Link
                        to="/best-sellers"
                        className="overline flex items-center gap-2 hover:text-[var(--vl-rose)]"
                        style={{ color: "var(--vl-text)" }}
                    >
                        Shop all <ArrowUpRight size={14} strokeWidth={1.5} />
                    </Link>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                    {best.map((p, i) => (
                        <ProductCard key={p.id} product={p} index={i} />
                    ))}
                </div>
            </section>

            {/* Shop by category — bento */}
            <section
                className="max-w-[1440px] mx-auto px-6 md:px-12 pb-24 md:pb-32"
                data-testid="featured-categories"
            >
                <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
                    <div>
                        <div className="overline mb-3">02 · The atelier</div>
                        <h2
                            className="font-serif text-4xl sm:text-6xl tracking-tight"
                            style={{ color: "var(--vl-text)" }}
                        >
                            Shop by{" "}
                            <em
                                style={{
                                    color: "var(--vl-rose)",
                                    fontStyle: "italic",
                                }}
                            >
                                category.
                            </em>
                        </h2>
                    </div>
                    <Link
                        to="/categories"
                        className="overline flex items-center gap-2 hover:text-[var(--vl-rose)]"
                        style={{ color: "var(--vl-text)" }}
                    >
                        View all <ArrowUpRight size={14} strokeWidth={1.5} />
                    </Link>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 lg:gap-5 auto-rows-[240px]">
                    {cats.slice(0, 6).map((c, idx) => (
                        <Link
                            key={c.slug}
                            to={`/shop?category=${c.slug}`}
                            data-testid={`featured-cat-${c.slug}`}
                            className={`relative overflow-hidden group ${
                                idx === 0
                                    ? "col-span-2 row-span-2 lg:col-span-3"
                                    : idx === 3
                                      ? "lg:col-span-2"
                                      : ""
                            }`}
                            style={{ background: "var(--vl-surface)" }}
                        >
                            <img
                                src={c.image}
                                alt={c.name}
                                className="vl-img absolute inset-0 w-full h-full object-cover"
                                style={{ filter: "brightness(0.6)" }}
                            />
                            <div
                                className="absolute inset-0"
                                style={{
                                    background:
                                        "linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(10,10,11,0.88) 100%)",
                                }}
                            />
                            <div className="relative h-full flex flex-col justify-end p-5">
                                <div
                                    className="overline mb-2"
                                    style={{ color: "var(--vl-rose)" }}
                                >
                                    {c.tagline}
                                </div>
                                <div
                                    className="font-serif text-2xl sm:text-3xl tracking-tight"
                                    style={{ color: "var(--vl-text)" }}
                                >
                                    {c.name}
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* The Beginner Edit — editorial split */}
            <section
                className="relative"
                style={{
                    borderTop: "1px solid var(--vl-border)",
                    borderBottom: "1px solid var(--vl-border)",
                    background:
                        "linear-gradient(180deg, var(--vl-bg) 0%, var(--vl-surface) 100%)",
                }}
                data-testid="beginner-edit"
            >
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-32 grid md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-5">
                        <div className="overline mb-3">03 · Gently begin</div>
                        <h2
                            className="font-serif text-4xl sm:text-6xl tracking-tight"
                            style={{ color: "var(--vl-text)" }}
                        >
                            The Beginner{" "}
                            <em
                                style={{
                                    color: "var(--vl-rose)",
                                    fontStyle: "italic",
                                }}
                            >
                                Edit.
                            </em>
                        </h2>
                        <p
                            className="mt-6 max-w-md text-base"
                            style={{
                                color: "var(--vl-muted)",
                                lineHeight: 1.85,
                            }}
                        >
                            Unhurried pieces for a first ritual. No
                            expectations, no pressure — only pieces we'd
                            quietly recommend to a close friend.
                        </p>
                        <Link
                            to="/shop?category=beginner-collection"
                            className="vl-btn vl-btn-ghost mt-10"
                            data-testid="beginner-edit-cta"
                        >
                            Shop the Beginner Edit
                        </Link>
                    </div>
                    <div className="md:col-span-7 grid grid-cols-2 gap-5">
                        {beginner.map((p, i) => (
                            <ProductCard key={p.id} product={p} index={i} />
                        ))}
                    </div>
                </div>
            </section>

            {/* The Luxury Edit — dark editorial */}
            <section
                className="relative overflow-hidden"
                data-testid="luxury-edit"
                style={{ background: "#060607" }}
            >
                <img
                    src={SILK_RED}
                    alt=""
                    aria-hidden
                    className="absolute inset-0 w-full h-full object-cover opacity-25"
                    style={{ filter: "brightness(0.6)" }}
                />
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "radial-gradient(ellipse at 20% 30%, rgba(92,29,42,0.35) 0%, rgba(6,6,7,0.95) 60%)",
                    }}
                />
                <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-32 grid md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-7 grid grid-cols-2 gap-5 order-2 md:order-1">
                        {luxury.map((p, i) => (
                            <ProductCard key={p.id} product={p} index={i} />
                        ))}
                    </div>
                    <div className="md:col-span-5 order-1 md:order-2">
                        <div className="overline mb-3">04 · Private reserve</div>
                        <h2
                            className="font-serif text-4xl sm:text-6xl tracking-tight"
                            style={{ color: "var(--vl-text)" }}
                        >
                            The Luxury{" "}
                            <em
                                style={{
                                    color: "var(--vl-rose)",
                                    fontStyle: "italic",
                                }}
                            >
                                Edit.
                            </em>
                        </h2>
                        <p
                            className="mt-6 max-w-md text-base"
                            style={{
                                color: "var(--vl-muted)",
                                lineHeight: 1.85,
                            }}
                        >
                            Numbered, hand-finished, limited to a small
                            allocation. Our most considered pieces — each
                            one presented in a velvet keepsake box.
                        </p>
                        <Link
                            to="/shop?category=luxury-collection"
                            className="vl-btn vl-btn-solid mt-10"
                            data-testid="luxury-edit-cta"
                        >
                            Explore the Luxury Edit
                        </Link>
                    </div>
                </div>
            </section>

            {/* Why Veloura */}
            <section
                className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-32"
                data-testid="why-veloura"
            >
                <div className="grid md:grid-cols-12 gap-10">
                    <div className="md:col-span-5">
                        <div className="overline mb-4">05 · Why Veloura</div>
                        <h2
                            className="font-serif text-4xl sm:text-5xl tracking-tight"
                            style={{ color: "var(--vl-text)" }}
                        >
                            A studio built on{" "}
                            <em
                                style={{
                                    color: "var(--vl-rose)",
                                    fontStyle: "italic",
                                }}
                            >
                                trust,
                            </em>{" "}
                            taste, and quiet confidence.
                        </h2>
                        <p
                            className="mt-6 text-base max-w-md"
                            style={{
                                color: "var(--vl-muted)",
                                lineHeight: 1.85,
                            }}
                        >
                            Every formulation dermatologist-reviewed. Every
                            material body-safe. Every box unbranded. We
                            treat intimate wellness the way other brands
                            treat fine skincare — with rigour, taste, and a
                            quieter voice.
                        </p>
                    </div>

                    <div className="md:col-span-7 grid sm:grid-cols-2 gap-4">
                        {[
                            {
                                icon: (
                                    <ShieldCheck
                                        size={22}
                                        strokeWidth={1.25}
                                    />
                                ),
                                t: "100% Discreet Shipping",
                                d: "Unbranded mailer. Return label reads V. Atelier.",
                            },
                            {
                                icon: (
                                    <PackageCheck
                                        size={22}
                                        strokeWidth={1.25}
                                    />
                                ),
                                t: "Dermatologist-Tested",
                                d: "Every serum, oil, and lubricant clinically reviewed.",
                            },
                            {
                                icon: (
                                    <Heart size={22} strokeWidth={1.25} />
                                ),
                                t: "Body-Safe Materials",
                                d: "Medical-grade silicone. Never phthalates.",
                            },
                            {
                                icon: (
                                    <Sparkles
                                        size={22}
                                        strokeWidth={1.25}
                                    />
                                ),
                                t: "Private Concierge",
                                d: "A WhatsApp line for every question.",
                            },
                        ].map((f) => (
                            <div
                                key={f.t}
                                className="p-6"
                                style={{
                                    background: "var(--vl-surface)",
                                    border: "1px solid var(--vl-border)",
                                }}
                            >
                                <div style={{ color: "var(--vl-rose)" }}>
                                    {f.icon}
                                </div>
                                <div
                                    className="font-serif text-2xl mt-5"
                                    style={{ color: "var(--vl-text)" }}
                                >
                                    {f.t}
                                </div>
                                <p
                                    className="text-sm mt-2"
                                    style={{
                                        color: "var(--vl-muted)",
                                        lineHeight: 1.7,
                                    }}
                                >
                                    {f.d}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Discreet shipping showcase */}
            <section
                className="relative"
                style={{
                    background: "var(--vl-surface)",
                    borderTop: "1px solid var(--vl-border)",
                    borderBottom: "1px solid var(--vl-border)",
                }}
                data-testid="discreet-shipping"
            >
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-32 grid md:grid-cols-2 items-center gap-14">
                    <div style={{ background: "var(--vl-bg)" }}>
                        <img
                            src={DISCREET_BOX}
                            alt="Matte black unbranded Veloura package"
                            className="w-full aspect-[4/5] object-cover"
                        />
                    </div>
                    <div>
                        <div className="overline mb-4">
                            06 · Privacy, by design
                        </div>
                        <h2
                            className="font-serif text-4xl sm:text-5xl tracking-tight"
                            style={{ color: "var(--vl-text)" }}
                        >
                            What arrives{" "}
                            <em
                                style={{
                                    color: "var(--vl-rose)",
                                    fontStyle: "italic",
                                }}
                            >
                                at your door.
                            </em>
                        </h2>
                        <p
                            className="mt-6 text-base max-w-md"
                            style={{
                                color: "var(--vl-muted)",
                                lineHeight: 1.85,
                            }}
                        >
                            A plain matte mailer. No logo. No product name.
                            No suggestive copy. Inside, each piece is
                            tissue-wrapped, sealed, and presented in a matte
                            keepsake box.
                        </p>
                        <ul
                            className="mt-8 space-y-3 text-sm"
                            style={{ color: "var(--vl-text)" }}
                        >
                            {[
                                "Unbranded exterior mailer",
                                "Return address reads V. Atelier",
                                "Invoice never mentions product names",
                                "Cash on Delivery available across India",
                            ].map((x) => (
                                <li
                                    key={x}
                                    className="flex items-center gap-3"
                                >
                                    <span
                                        className="w-6 h-px"
                                        style={{
                                            background: "var(--vl-rose)",
                                        }}
                                    />
                                    {x}
                                </li>
                            ))}
                        </ul>
                        <Link
                            to="/faq"
                            className="vl-btn vl-btn-ghost mt-10"
                            data-testid="discreet-learn-more"
                        >
                            Shipping & privacy FAQ
                        </Link>
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section
                className="relative overflow-hidden"
                data-testid="testimonials"
                style={{ background: "var(--vl-bg)" }}
            >
                <img
                    src={SCULPTURE}
                    alt=""
                    aria-hidden
                    className="absolute inset-0 w-full h-full object-cover opacity-15"
                />
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(180deg, var(--vl-bg) 0%, rgba(10,10,11,0.8) 100%)",
                    }}
                />
                <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-32">
                    <div className="overline mb-4 text-center">
                        07 · From our community
                    </div>
                    <h2
                        className="font-serif text-4xl sm:text-5xl tracking-tight text-center mb-16"
                        style={{ color: "var(--vl-text)" }}
                    >
                        Quietly{" "}
                        <em
                            style={{
                                color: "var(--vl-rose)",
                                fontStyle: "italic",
                            }}
                        >
                            adored.
                        </em>
                    </h2>
                    <div className="grid md:grid-cols-3 gap-8">
                        {TESTIMONIALS.map((t, i) => (
                            <div
                                key={t.a}
                                className="p-8"
                                style={{
                                    background: "var(--vl-surface)",
                                    border: "1px solid var(--vl-border)",
                                }}
                                data-testid={`testimonial-${i}`}
                            >
                                <Quote
                                    size={22}
                                    strokeWidth={1.25}
                                    color="var(--vl-rose)"
                                />
                                <p
                                    className="font-serif text-xl mt-5 leading-snug"
                                    style={{ color: "var(--vl-text)" }}
                                >
                                    “{t.q}”
                                </p>
                                <div
                                    className="overline mt-6"
                                    style={{ color: "var(--vl-muted)" }}
                                >
                                    — {t.a}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Newsletter */}
            <section
                className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-32 text-center"
                data-testid="newsletter-section"
            >
                <div className="overline mb-4">The Veloura Journal</div>
                <h2
                    className="font-serif text-4xl sm:text-6xl tracking-tight max-w-3xl mx-auto"
                    style={{ color: "var(--vl-text)" }}
                >
                    Intimate wellness education,{" "}
                    <em
                        style={{
                            color: "var(--vl-rose)",
                            fontStyle: "italic",
                        }}
                    >
                        privately delivered.
                    </em>
                </h2>
                <p
                    className="mt-5 max-w-md mx-auto"
                    style={{
                        color: "var(--vl-muted)",
                        lineHeight: 1.8,
                    }}
                >
                    Once a month. Never a sales pitch. Unsubscribe in a
                    single tap.
                </p>
                <form
                    onSubmit={subscribe}
                    className="mt-10 max-w-md mx-auto flex items-end gap-3"
                    data-testid="newsletter-form-home"
                >
                    <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@private.com"
                        className="vl-input"
                        data-testid="newsletter-email-home"
                    />
                    <button
                        type="submit"
                        className="vl-btn vl-btn-solid"
                        data-testid="newsletter-submit-home"
                    >
                        Subscribe
                    </button>
                </form>
            </section>
        </div>
    );
}
